import { useCallback, useEffect, useState } from 'react';
import { Button, Card, Alert, Loader, Badge, Select } from '@dreamtree-org/twreact-ui';
import { GitCompareArrows, GitPullRequest } from 'lucide-react';
import Can from '@rbac/Can';
import { useGitApp, errText } from './useGitApp';

const STATUS_VARIANT = { added: 'success', modified: 'primary', removed: 'error', renamed: 'warning' };
const selVal = (v) => (v?.target ? v.target.value : (v?.value ?? v ?? ''));

/** Colourises a unified diff hunk. Purely presentational — the patch text is authoritative. */
function Patch({ text }) {
  return (
    <pre className="text-[11px] font-mono bg-secondary-50 dark:bg-secondary-800 rounded p-3 overflow-x-auto max-h-96">
      {text.split('\n').map((line, i) => {
        let cls = 'opacity-80';
        if (line.startsWith('+') && !line.startsWith('+++')) cls = 'text-success-700 bg-success-50 dark:bg-success-900/20';
        else if (line.startsWith('-') && !line.startsWith('---')) cls = 'text-error-700 bg-error-50 dark:bg-error-900/20';
        else if (line.startsWith('@@')) cls = 'text-primary-600 font-semibold';
        // Index key is correct here: a diff hunk is an immutable, ordered snapshot —
        // the list is never reordered or spliced, only replaced wholesale.
        return <div key={i} className={cls}>{line || ' '}</div>;
      })}
    </pre>
  );
}

/** Branch comparison, and the entry point for opening a PR from what you're looking at. */
export default function GitDiff({ appId, branches, branch, onOpenedPr }) {
  const { act } = useGitApp(appId);

  const [base, setBase] = useState('');
  const [head, setHead] = useState(branch || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');

  const compare = useCallback(async () => {
    setLoading(true);
    setErr('');
    setNotice('');
    try {
      setData(await act('diff', { ...(base ? { base } : {}), ...(head ? { head } : {}) }));
    } catch (ex) {
      setErr(errText(ex, 'Could not compare branches'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [act, base, head]);

  useEffect(() => { compare(); }, [compare]);

  const openPr = async () => {
    if (!data || data.base === data.head) return;
    setCreating(true);
    setErr('');
    try {
      const r = await act('createPr', { head: data.head, base: data.base });
      setNotice(`Opened PR #${r.number}.`);
      if (onOpenedPr) onOpenedPr();
    } catch (ex) {
      setErr(errText(ex, 'Could not open a pull request'));
    } finally {
      setCreating(false);
    }
  };

  const options = (branches || []).map((b) => ({ value: b.name, label: b.name }));

  return (
    <div className="space-y-4">
      <Card className="p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <Select label="Base (merge into)" value={base} options={options} onChange={(v) => setBase(selVal(v))} placeholder="Repository default" />
          <Select label="Head (compare from)" value={head} options={options} onChange={(v) => setHead(selVal(v))} placeholder="App branch" />
          <div className="flex gap-2">
            <Button onClick={compare} loading={loading} leftIcon={<GitCompareArrows size={16} />}>Compare</Button>
            {data && data.base !== data.head && data.files?.length ? (
              <Can module="development" action="commit">
                <Button variant="secondary" onClick={openPr} loading={creating} leftIcon={<GitPullRequest size={16} />}>Open PR</Button>
              </Can>
            ) : null}
          </div>
        </div>
      </Card>

      {err ? <Alert variant="error" title="Diff">{err}</Alert> : null}
      {notice ? <Alert variant="success" title="Diff">{notice}</Alert> : null}

      {loading && <Loader text="Comparing…" />}

      {!loading && data ? (
        <Card className="p-4 space-y-3">
          <div className="flex items-center gap-3 flex-wrap text-sm">
            <span className="font-mono">{data.base} ← {data.head}</span>
            {data.status ? <Badge variant="secondary">{data.status}</Badge> : null}
            {data.ahead_by != null ? <span className="opacity-70">{data.ahead_by} ahead</span> : null}
            <span className="opacity-70">{data.files?.length || 0} file(s)</span>
          </div>

          {data.note ? <p className="text-sm opacity-60">{data.note}</p> : null}
          {!data.note && !data.files?.length ? <p className="text-sm opacity-60">No differences.</p> : null}

          {(data.files || []).map((f) => (
            <div key={f.filename} className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant={STATUS_VARIANT[f.status] || 'default'}>{f.status}</Badge>
                <span className="font-mono text-xs break-all">{f.filename}</span>
                <span className="text-[11px] text-success-700">+{f.additions}</span>
                <span className="text-[11px] text-error-700">−{f.deletions}</span>
              </div>
              {f.patch ? <Patch text={f.patch} /> : <p className="text-[11px] opacity-50">No inline patch (binary or too large).</p>}
            </div>
          ))}
        </Card>
      ) : null}
    </div>
  );
}
